if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.pointers = new Map();
        this.zoom = { scale: 1, x: 0, y: 0 };
        this.pinchStart = null;
        this.lastTap = 0;
      }

      /* GSAP lo carga main-product.liquid. Si no esta, o el usuario pidio menos
         movimiento, abre y cierra sin animar. */
      canAnimate() {
        return typeof gsap !== 'undefined' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      activeMedia() {
        return this.querySelector('.product-media-modal__content > .active');
      }

      activeImage() {
        const media = this.activeMedia();
        return media ? media.querySelector('img') : null;
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.resetZoom(false);
        this.animateIn();
        this.bindZoom();
      }

      hide() {
        const media = this.activeMedia();
        this.resetZoom(false);
        if (!this.canAnimate() || !media) return super.hide();

        gsap.killTweensOf([this, media]);
        gsap.to(media, { scale: 0.94, opacity: 0, duration: 0.18, ease: 'power2.in' });
        gsap.to(this, {
          opacity: 0,
          duration: 0.2,
          ease: 'power2.in',
          onComplete: () => {
            /* clearProps antes de cerrar: si no, el opacity:0 inline queda
               pegado y la proxima apertura no se ve. */
            gsap.set([this, media], { clearProps: 'opacity,transform' });
            super.hide();
          },
        });
      }

      /* La foto arranca exactamente donde esta la miniatura que tocaste y crece
         hasta su lugar final: se entiende que es LA MISMA foto agrandandose, no
         una pantalla nueva. */
      animateIn() {
        const media = this.activeMedia();
        if (!this.canAnimate() || !media) return;

        gsap.killTweensOf([this, media]);
        gsap.fromTo(this, { opacity: 0 }, { opacity: 1, duration: 0.22, ease: 'power2.out' });

        const origen = this.openerRect();
        const destino = media.getBoundingClientRect();
        if (!origen || !destino.width || !destino.height) {
          gsap.fromTo(media, { scale: 0.9, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.4, ease: 'power3.out' });
          return;
        }

        gsap.fromTo(
          media,
          {
            x: origen.left + origen.width / 2 - (destino.left + destino.width / 2),
            y: origen.top + origen.height / 2 - (destino.top + destino.height / 2),
            scaleX: origen.width / destino.width,
            scaleY: origen.height / destino.height,
            opacity: 0.35,
          },
          {
            x: 0,
            y: 0,
            scaleX: 1,
            scaleY: 1,
            opacity: 1,
            duration: 0.45,
            ease: 'power3.out',
            clearProps: 'transform',
          }
        );
      }

      /* Rect de la miniatura desde la que se abrio el modal */
      openerRect() {
        if (!this.openedBy) return null;
        const scope = this.openedBy.closest('.product__media-item') || this.openedBy.parentElement;
        const img = scope ? scope.querySelector('img, video, iframe') : null;
        const rect = (img || this.openedBy).getBoundingClientRect();
        return rect.width && rect.height ? rect : null;
      }

      /* ---------- Zoom dentro del modal ---------- */
      /* El pellizco de la pagina esta desactivado, asi que la foto grande se
         amplia con su propio gesto: pellizco, arrastre y doble toque. */
      bindZoom() {
        if (this.zoomBound) return;
        this.zoomBound = true;
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        content.addEventListener('pointerdown', (e) => this.onDown(e));
        content.addEventListener('pointermove', (e) => this.onMove(e));
        ['pointerup', 'pointercancel', 'pointerleave'].forEach((tipo) =>
          content.addEventListener(tipo, (e) => this.onUp(e))
        );
        content.addEventListener('dblclick', (e) => this.toggleZoom(e));
      }

      onDown(e) {
        const img = this.activeImage();
        if (!img) return;
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (this.pointers.size === 2) {
          const [a, b] = [...this.pointers.values()];
          this.pinchStart = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: this.zoom.scale };
        } else if (this.pointers.size === 1) {
          this.dragStart = { x: e.clientX, y: e.clientY, ox: this.zoom.x, oy: this.zoom.y };
          /* doble toque: alterna 1x y 2.5x */
          const ahora = Date.now();
          if (ahora - this.lastTap < 300) this.toggleZoom(e);
          this.lastTap = ahora;
        }
      }

      onMove(e) {
        if (!this.pointers.has(e.pointerId)) return;
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (this.pointers.size === 2 && this.pinchStart) {
          const [a, b] = [...this.pointers.values()];
          const dist = Math.hypot(a.x - b.x, a.y - b.y);
          this.zoom.scale = this.clampScale((dist / this.pinchStart.dist) * this.pinchStart.scale);
          this.applyZoom();
          e.preventDefault();
        } else if (this.pointers.size === 1 && this.zoom.scale > 1 && this.dragStart) {
          this.zoom.x = this.dragStart.ox + (e.clientX - this.dragStart.x);
          this.zoom.y = this.dragStart.oy + (e.clientY - this.dragStart.y);
          this.clampPan();
          this.applyZoom();
          e.preventDefault();
        }
      }

      onUp(e) {
        this.pointers.delete(e.pointerId);
        if (this.pointers.size < 2) this.pinchStart = null;
        if (this.pointers.size === 0 && this.zoom.scale <= 1) this.resetZoom(true);
      }

      toggleZoom(e) {
        this.zoom.scale = this.zoom.scale > 1 ? 1 : 2.5;
        if (this.zoom.scale === 1) {
          this.zoom.x = 0;
          this.zoom.y = 0;
        }
        this.applyZoom(true);
        if (e) e.preventDefault();
      }

      clampScale(s) {
        return Math.min(4, Math.max(1, s));
      }

      /* que no se pueda arrastrar la foto fuera de la caja */
      clampPan() {
        const img = this.activeImage();
        if (!img) return;
        const r = img.getBoundingClientRect();
        const maxX = Math.max(0, (r.width * this.zoom.scale - r.width) / 2);
        const maxY = Math.max(0, (r.height * this.zoom.scale - r.height) / 2);
        this.zoom.x = Math.min(maxX, Math.max(-maxX, this.zoom.x));
        this.zoom.y = Math.min(maxY, Math.max(-maxY, this.zoom.y));
      }

      applyZoom(animar) {
        const img = this.activeImage();
        if (!img) return;
        const props = { scale: this.zoom.scale, x: this.zoom.x, y: this.zoom.y };
        if (animar && this.canAnimate()) {
          gsap.to(img, { ...props, duration: 0.3, ease: 'power3.out' });
        } else if (typeof gsap !== 'undefined') {
          gsap.set(img, props);
        } else {
          img.style.transform = `translate(${this.zoom.x}px, ${this.zoom.y}px) scale(${this.zoom.scale})`;
        }
      }

      resetZoom(animar) {
        this.zoom = { scale: 1, x: 0, y: 0 };
        this.pointers.clear();
        this.pinchStart = null;
        const img = this.activeImage();
        if (!img) return;
        if (animar && this.canAnimate()) gsap.to(img, { scale: 1, x: 0, y: 0, duration: 0.25, ease: 'power3.out' });
        else if (typeof gsap !== 'undefined') gsap.set(img, { clearProps: 'transform' });
        else img.style.transform = '';
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
